import { createContext, type FC, type ReactNode, useCallback, useContext, useMemo } from 'react';

import { markLeavingPage } from '../lib/navigation/pageExit';
import { useEnvConfig } from './ConfigProvider';

/**
 * Minimal "WorkOS session" that the rest of the SPA can read.
 *
 * Why we don't expose the full WorkOS user shape: the SPA's source of truth for the *user*
 * is still the Altitude `User` row hydrated by `ViewerProvider`. AuthKit owns the credential
 * half of the contract — it tells us "yes, this person is signed in, here is their access
 * token" — and we resolve that to the local row separately.
 */
type AuthKitSession = {
  /** The current WorkOS access token, attached as a Bearer on every API call. */
  accessToken: string | null;
  /** WorkOS user identity (sub + email) once signed in. Null when signed out. */
  user: { sub: string; email?: string } | null;
};

type AuthKitContextValue = AuthKitSession & {
  /** Optional `returnTo` — relative path the AuthKit callback should land on after sign-in. */
  signIn: (returnTo?: string) => void;
  signUp: () => void;
  signOut: () => void;
};

const AuthKitContext = createContext<AuthKitContextValue | undefined>(undefined);

/**
 * Local-storage keys for the bypass session — the test/Cypress flow stores a sub +
 * access token here directly so the rest of the SPA doesn't need to know whether the
 * session came from AuthKit or from a `/test-auth/{userId}` round-trip.
 */
const BYPASS_TOKEN_KEY = 'altitude:auth:bypass:token';
const BYPASS_SUB_KEY = 'altitude:auth:bypass:sub';
const BYPASS_EMAIL_KEY = 'altitude:auth:bypass:email';

/** How long to wait for the backend's logout URL before giving up and redirecting locally. */
const SIGN_OUT_TIMEOUT_MS = 8000;

function readBypassSession(): AuthKitSession {
  const accessToken = localStorage.getItem(BYPASS_TOKEN_KEY);
  const sub = localStorage.getItem(BYPASS_SUB_KEY);
  if (!accessToken || !sub) {
    return { accessToken: null, user: null };
  }
  const email = localStorage.getItem(BYPASS_EMAIL_KEY) ?? undefined;
  return { accessToken, user: { sub, email } };
}

/**
 * SPA bridge to WorkOS AuthKit.
 *
 * Why this provider sits above ApolloProviderInit: the Apollo `authLink` reads the access
 * token via `useAuthKit()` so every GraphQL request carries the right Bearer header.
 * Putting AuthKit lower in the tree would force authLink to read from a side channel
 * (localStorage today, before the migration), which is exactly what we are removing.
 *
 * Implementation note: the production wiring uses `@workos-inc/authkit-react`. To keep
 * this PR independent of WorkOS dev-tenant credentials being available, the provider
 * currently reads a simple bypass session from localStorage. Once the shared dev tenant
 * is provisioned, swap the body of this provider for the AuthKit hook (`useAuth()`) — the
 * public surface (`signIn`, `signOut`, `accessToken`, `user`) is what the rest of the SPA
 * already consumes.
 */
export const AuthKitProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const config = useEnvConfig();

  const session = readBypassSession();

  /**
   * Sign-in / sign-up are top-level browser navigations to the backend, which builds the
   * AuthKit authorize URL with a CSRF state cookie and redirects from there. Doing this
   * server-side rather than client-side gives us a single place to mint state, validate
   * it on the callback, and set the encrypted session cookie — none of which is safe to
   * do from JS in the browser.
   */
  const signIn = useCallback(
    (returnTo?: string) => {
      const url = new URL(`${config.backendUrl}/auth/sign-in`);
      // Only forward relative paths — protects against open-redirect attacks if a third
      // party crafts a `?return_to=https://evil.example.com` link to /login.
      if (returnTo?.startsWith('/')) {
        url.searchParams.set('return_to', returnTo);
      }
      markLeavingPage();
      window.location.assign(url.toString());
    },
    [config.backendUrl],
  );

  const signUp = useCallback(() => {
    markLeavingPage();
    window.location.assign(`${config.backendUrl}/auth/sign-in?screen_hint=sign-up`);
  }, [config.backendUrl]);

  const signOut = useCallback(async () => {
    // Announced up front rather than next to the redirect below: every path out of this
    // callback ends in a full-page navigation, so from here on any crash is a symptom of the
    // page being torn down and must not be reported to the user as a failure.
    markLeavingPage();
    // Clear bypass-mode session keys (no-op in real WorkOS mode).
    localStorage.removeItem(BYPASS_TOKEN_KEY);
    localStorage.removeItem(BYPASS_SUB_KEY);
    localStorage.removeItem(BYPASS_EMAIL_KEY);
    // Tell the backend to clear the HttpOnly cookie and hand back the AuthKit logout URL.
    // Without redirecting through that URL, AuthKit's own session cookie stays alive and the
    // very next `signIn()` would SSO the user straight back in.
    let logoutUrl: string | undefined;
    try {
      const res = await fetch(`${config.backendUrl}/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
        // Bounded because `/logout` now waits on this call instead of navigating away on its
        // own: a request that never settles would leave the user on a spinner forever, where
        // before it merely lost a race it was never meant to be in.
        signal: AbortSignal.timeout(SIGN_OUT_TIMEOUT_MS),
      });
      const body = (await res.json().catch(() => ({}))) as { logoutUrl?: string };
      logoutUrl = body.logoutUrl;
    } catch {
      // Network failure or timeout: fall through to the local /login redirect. The backend
      // cookie may still be set, but the user clicked Logout, so the local UX should match.
    }
    window.location.assign(logoutUrl ?? '/login');
  }, [config.backendUrl]);

  const value = useMemo<AuthKitContextValue>(
    () => ({
      accessToken: session.accessToken,
      user: session.user,
      signIn,
      signUp,
      signOut,
    }),
    [session.accessToken, session.user, signIn, signUp, signOut],
  );

  return <AuthKitContext.Provider value={value}>{children}</AuthKitContext.Provider>;
};

export function useAuthKit(): AuthKitContextValue {
  const ctx = useContext(AuthKitContext);
  if (!ctx) {
    throw new Error('useAuthKit must be used within an AuthKitProvider');
  }
  return ctx;
}

/**
 * Internal helper used by the bypass-mode `cy.login()` Cypress command and by integration
 * tests to seed a session directly. Not exported from the SPA's public surface.
 */
export function setBypassSession(opts: { accessToken: string; sub: string; email?: string }): void {
  localStorage.setItem(BYPASS_TOKEN_KEY, opts.accessToken);
  localStorage.setItem(BYPASS_SUB_KEY, opts.sub);
  if (opts.email) {
    localStorage.setItem(BYPASS_EMAIL_KEY, opts.email);
  }
}
